import {noop} from '@benchling/js-utils';

import EntryActions from 'actions/EntryActions';
import * as analytics from 'analytics/Analytics';
import {AnalyticsEventMetadata} from 'analytics/AnalyticsTypes';
import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {callAsyncWithDataModifications} from 'audit/DataModificationsModal';
import {InventoryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {FixedPlateModalType} from 'constants/analytics-products/SamplesAnalyticsConstants';
import {ActionOrigin, STORABLE_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {FullyAccessibleGrid} from 'constants/SampleConstants';
import {SchemaTag} from 'folder-items/FileConstants';
import DocumentActions from 'notebook/documents/DocumentActions';
import {
  annotateFillFixedPlate,
  AnnotateFillFixedPlateParams,
  annotateFillNewFixedPlate,
  AnnotateFillNewFixedPlateParams,
  CreateFixedPlateParams,
} from 'notebook/integrations/plate-maps/FixedPlateEditorDAO';
import {NotePartIndex} from 'notebook/util/EntryNoteIndexUtil';
import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  PLATE_ANNOTATE_FILL_STARTED_EVENT,
  PLATE_ANNOTATE_FILL_FINISHED_EVENT,
  getGridSizeBucket,
} from 'performance/processInventoryEvents';
import {EventMetadata} from 'performance/ProfilerConstants';
import {CreatePlatePaneState} from 'samples/CreatePlatePane';
import {
  getInventoryRegistryChangeSummary,
  InventoryRegistryDataModAction,
} from 'samples/getInventoryRegistryChangeSummary';
import {
  getAnnotateFixedPlateInputsFromPlateMap,
  getAverageContentsAddedPerWell,
  getModifiedStorableIdsForTransfer,
  getSchemaTagUpdateParams,
  getTransferToFixedPlateInputsFromPlateMap,
} from 'samples/plate-maps/AnnotateFillPlateUtil';
import {PlateDesignVersionRef} from 'samples/plate-maps/plate-record/PlateRecordConstants';
import {PlateMap} from 'samples/plate-maps/PlateMapConstants';
import {invalidateGrid} from 'samples/StorableActions';
import APIUtil from 'util/APIUtil';
import {withLoadingScreen} from 'util/Promises';
import {MakeFieldsRequired} from 'util/Types';

// TODO(BNCH-95368): Use a standard method of refreshing an entry.
async function refreshEntry(fileId: string): Promise<void> {
  const entryType = APIUtil.apiIdToType(fileId);
  if (entryType === ModelTypeConstants.DOCUMENT) {
    await DocumentActions.refresh(fileId);
  } else if (entryType === ModelTypeConstants.ENTRY) {
    await EntryActions.open(fileId);
  } else {
    assert(APIUtil.isExecutionDocument(fileId), 'Unexpected file type', {fileId});
  }
}

/**
 * Whether plate maps are supported for the given entry.
 */
export function allowPlateMaps({fileId, isConfig}: {fileId: string; isConfig: boolean}): boolean {
  return allowPlateDesigns({fileId, isConfig}) || allowPlateRecords({fileId, isConfig});
}

function arePlateMapRequirementsEnabled(): boolean {
  return (
    isFeatureEnabled({feature: InventoryFeature.INVENTORY_EDIT}) &&
    isFeatureEnabled({feature: InventoryFeature.INVENTORY_CREATE})
  );
}

/**
 * Determines if a file is a supported template-like (template, subtemplate, or config document).
 */
export function isSupportedDocumentTemplateLike({
  fileId,
  isConfig,
}: {
  fileId: string;
  isConfig: boolean;
}): boolean {
  return (
    APIUtil.isEntryTemplate(fileId) ||
    APIUtil.isSubtemplate(fileId) ||
    (APIUtil.isDocument(fileId) && isConfig)
  );
}

/**
 * Determines if a file is a supported document-like (entry or non-config document).
 */
export function isSupportedDocumentLike({fileId, isConfig}: {fileId: string; isConfig: boolean}): boolean {
  return APIUtil.isEntry(fileId) || (APIUtil.isDocument(fileId) && !isConfig);
}

/**
 * Whether plate designs are supported for the given entry.
 *
 * Plate designs are supported for:
 * - Entries, entry templates, entry sub templates, and recipes
 */
export function allowPlateDesigns({fileId, isConfig}: {fileId?: string; isConfig: boolean}): boolean {
  if (fileId == null) {
    return false;
  }
  const arePlateMapReqsEnabled = arePlateMapRequirementsEnabled();
  const isSupported =
    isSupportedDocumentLike({fileId, isConfig}) || isSupportedDocumentTemplateLike({fileId, isConfig});

  return arePlateMapReqsEnabled && isSupported;
}

/**
 * Whether plate records are supported for the given entry.
 *
 * Plate records are only supported for entries and non-recipe documents.
 */
export function allowPlateRecords({fileId, isConfig}: {fileId?: string; isConfig: boolean}): boolean {
  if (fileId == null) {
    return false;
  }
  return arePlateMapRequirementsEnabled() && isSupportedDocumentLike({fileId, isConfig});
}

/**
 * Whether the contents layer should be enabled for a plate design in the given entry or document.
 *
 * Currently, the contents layer of a plate design is enabled for entries and non-recipe documents.
 *
 * @param fileId - The file ID of the entry or document
 * @param isConfig - Whether the file is a configuration document
 */
export function enablePlateDesignContentsLayer({
  fileId,
  isConfig,
}: {
  fileId: string;
  isConfig: boolean;
}): boolean {
  return APIUtil.isEntry(fileId) || (APIUtil.isDocument(fileId) && !isConfig);
}

function getCreateFixedPlateParams(
  paneState: MakeFieldsRequired<CreatePlatePaneState, 'selectedSchema'>
): CreateFixedPlateParams {
  return {
    schema_id: paneState.selectedSchema.id,
    name: paneState.name,
    barcode: paneState.customBarcode ?? null,
    location_id: paneState.selectedParent?.storable?.id ?? null,
    tags: Object.fromEntries(
      paneState.tags.map((tag) => [tag.tagSchemaField.apiId, getSchemaTagUpdateParams(tag)])
    ),
    study_ids: paneState.studyIds == null ? null : paneState.studyIds,
    source_id: paneState.sourceId ?? null,
  };
}

function getProfilerMetadata({
  plateMap,
  numberSchemaFieldsAnnotated,
  fileId,
  isNewPlate,
  fromPlateDesign,
}: {
  plateMap: PlateMap;
  numberSchemaFieldsAnnotated: number;
  fileId: string;
  isNewPlate: boolean;
  fromPlateDesign: boolean;
}): EventMetadata {
  const averageContentsAddedPerWell = getAverageContentsAddedPerWell(plateMap);
  const numberDataParametersAnnotated = Object.values(plateMap.wells[0].dataParameterTags).length;
  return {
    distributionMetadata: {
      plate_size_bucket: getGridSizeBucket(plateMap.wells.length),
      from_design: fromPlateDesign,
    },
    actionMetadata: {
      number_wells: plateMap.wells.length,
      average_items_transferred_per_well: averageContentsAddedPerWell,
      number_schema_fields_annotated: numberSchemaFieldsAnnotated,
      number_data_parameters_annotated: numberDataParametersAnnotated,
      origin: APIUtil.isDocument(fileId) ? ModelTypeConstants.WORKSHEET : ModelTypeConstants.ENTRY,
      is_new_plate: isNewPlate,
    },
  };
}

async function doCreateNewPlate({
  analyticsMetadata,
  callbackOnError,
  data,
  dataModsHeader,
  fileId,
  profilerEventMetadata,
}: {
  analyticsMetadata: AnalyticsEventMetadata;
  callbackOnError: () => void;
  data: AnnotateFillNewFixedPlateParams;
  dataModsHeader?: DataModificationsHeader;
  fileId: string;
  profilerEventMetadata: Record<string, unknown>;
}): Promise<void> {
  logProfilerTimingEvent(PLATE_ANNOTATE_FILL_STARTED_EVENT);
  let plateId;
  try {
    const response = await annotateFillNewFixedPlate(fileId, data, analyticsMetadata, dataModsHeader);
    plateId = response.plateId;
  } catch (err) {
    callbackOnError();
    throw err;
  }
  logProfilerTimingEvent(PLATE_ANNOTATE_FILL_FINISHED_EVENT, profilerEventMetadata);
  analytics.trackEvent(STORABLE_ANALYTICS_ACTIONS.SAVED_FIXED_PLATE_FROM_MODAL, {
    ...analyticsMetadata,
    fileId,
    modalType: FixedPlateModalType.FIXED_PLATE_EDITOR,
    plateDesignId: data.plateDesignVersionRef?.plateDesignApiId,
    plateDesignVersionId: data.plateDesignVersionRef?.plateDesignVersionApiId,
    plateId,
  });
  await refreshEntry(fileId);
}

/**
 * Create a new plate with the given annotations and contents, and insert a plate record in the given entry.
 */
export async function saveNewPlateWithPlateRecord({
  plateMap,
  createPlatePaneState,
  fileId,
  plateRecordName,
  notePartIndex,
  annotatedDataParameterIds,
  annotatedSchemaFieldIds,
  eventGroupId,
  plateDesignVersionRef,
  procedureMethodExecutionInstanceId,
  callbackOnError,
}: {
  plateMap: PlateMap;
  createPlatePaneState: MakeFieldsRequired<CreatePlatePaneState, 'selectedSchema'>;
  fileId: string;
  plateRecordName: string;
  notePartIndex: NotePartIndex;
  annotatedDataParameterIds: Set<string>;
  annotatedSchemaFieldIds: Set<string>;
  eventGroupId: string;
  plateDesignVersionRef: PlateDesignVersionRef | null;
  procedureMethodExecutionInstanceId: string | null;
  callbackOnError: () => void;
}): Promise<void> {
  const actionOrigin = APIUtil.isDocument(fileId) ? ActionOrigin.WORKSHEET : ActionOrigin.NOTEBOOK;

  const annotatePlateInputs = getAnnotateFixedPlateInputsFromPlateMap({
    plateMap,
    annotatedDataParameterIds,
    annotatedSchemaFieldIds,
    // We allow the use of measurement layers during the modal flow, but measurement layers
    // aren't applicable to plate records, so strip them out
    includeMeasurementLayers: false,
  });
  const fillFixedPlateInputs = getTransferToFixedPlateInputsFromPlateMap(plateMap);
  const createPlateParameters = getCreateFixedPlateParams(createPlatePaneState);
  const storableIds = getModifiedStorableIdsForTransfer(plateMap, null, false);
  const profilerEventMetadata = getProfilerMetadata({
    plateMap,
    numberSchemaFieldsAnnotated: annotatedSchemaFieldIds.size,
    fileId,
    isNewPlate: true,
    fromPlateDesign: plateDesignVersionRef != null,
  });

  // If there are no source containers modified for transfer, skip data modifications since this is a new plate.
  if (storableIds.length === 0) {
    await withLoadingScreen(
      doCreateNewPlate({
        analyticsMetadata: {actionOrigin, eventGroupId},
        callbackOnError,
        data: {
          annotations: annotatePlateInputs,
          transferInputs: fillFixedPlateInputs,
          plateParameters: createPlateParameters,
          plateRecordName,
          notePartIndex,
          plateDesignVersionRef,
          procedureMethodExecutionInstanceId,
        },
        fileId,
        profilerEventMetadata,
      }),
      'Creating plate'
    );
    return;
  }

  await callAsyncWithDataModifications({
    changeSummary: getInventoryRegistryChangeSummary({
      apiIds: storableIds,
      action: InventoryRegistryDataModAction.TRANSFER,
      containersMayBeWells: true,
    }),
    onPerformAction: async (dataModsHeader?) => {
      await withLoadingScreen(
        doCreateNewPlate({
          analyticsMetadata: {actionOrigin, eventGroupId},
          callbackOnError,
          data: {
            annotations: annotatePlateInputs,
            transferInputs: fillFixedPlateInputs,
            plateParameters: createPlateParameters,
            plateRecordName,
            notePartIndex,
            plateDesignVersionRef,
            procedureMethodExecutionInstanceId,
          },
          dataModsHeader,
          fileId,
          profilerEventMetadata,
        }),
        'Creating plate'
      );
      notifications.success('Annotated and filled plate');
    },
    onModalCancel: noop,
    analyticsParams: {
      module: 'SAMPLES',
      actionOrigin,
      modifiedAction: InventoryRegistryDataModAction.TRANSFER,
      numModifiedTotalItems: storableIds.length,
      entryId: null,
    },
    apiIds: storableIds,
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY,
  });
}

async function doUpdateFixedPlate({
  analyticsMetadata,
  fileId,
  callbackOnError,
  data,
  dataModsHeader,
  grid,
  profilerEventMetadata,
}: {
  analyticsMetadata: AnalyticsEventMetadata;
  callbackOnError: () => void;
  fileId: string;
  data: AnnotateFillFixedPlateParams;
  dataModsHeader?: DataModificationsHeader;
  grid: FullyAccessibleGrid;
  profilerEventMetadata: Record<string, unknown>;
}): Promise<void> {
  logProfilerTimingEvent(PLATE_ANNOTATE_FILL_STARTED_EVENT);
  try {
    await annotateFillFixedPlate(fileId, data, analyticsMetadata, dataModsHeader);
  } catch (err) {
    callbackOnError();
    throw err;
  }
  logProfilerTimingEvent(PLATE_ANNOTATE_FILL_FINISHED_EVENT, profilerEventMetadata);
  analytics.trackEvent(STORABLE_ANALYTICS_ACTIONS.SAVED_FIXED_PLATE_FROM_MODAL, {
    ...analyticsMetadata,
    fileId,
    modalType: FixedPlateModalType.FIXED_PLATE_EDITOR,
    plateDesignId: data.plateDesignVersionRef?.plateDesignApiId,
    plateId: grid.id,
  });
  invalidateGrid(grid.id);
  await refreshEntry(fileId);
}

/**
 * Save updated schema field values, annotations and transfer contents to an existing plate,
 * and insert a plate record in the given entry.
 */
export async function saveExistingPlateWithPlateRecord({
  plateMap,
  grid,
  plateSchemaFieldValues,
  fileId,
  plateRecordName,
  notePartIndex,
  annotatedDataParameterIds,
  annotatedSchemaFieldIds,
  eventGroupId,
  appendStudyIds,
  plateDesignVersionRef,
  procedureMethodExecutionInstanceId,
  callbackOnError,
}: {
  plateMap: PlateMap;
  grid: FullyAccessibleGrid;
  plateSchemaFieldValues: Array<SchemaTag>;
  fileId: string;
  plateRecordName: string;
  notePartIndex: NotePartIndex;
  annotatedDataParameterIds: Set<string>;
  annotatedSchemaFieldIds: Set<string>;
  eventGroupId: string;
  appendStudyIds: Array<string>;
  plateDesignVersionRef: PlateDesignVersionRef | null;
  procedureMethodExecutionInstanceId: string | null;
  callbackOnError: () => void;
}): Promise<void> {
  const actionOrigin = APIUtil.isDocument(fileId) ? ActionOrigin.WORKSHEET : ActionOrigin.NOTEBOOK;

  const annotatePlateInputs = getAnnotateFixedPlateInputsFromPlateMap({
    plateMap,
    annotatedDataParameterIds,
    annotatedSchemaFieldIds,
    // We allow the use of measurement layers during the modal flow, but measurement layers
    // aren't applicable to plate records, so strip them out
    includeMeasurementLayers: false,
  });
  const fillFixedPlateInputs = getTransferToFixedPlateInputsFromPlateMap(plateMap);
  const schemaFieldValuesToSet = Object.fromEntries(
    plateSchemaFieldValues.map((tag) => [tag.tagSchemaField.apiId, getSchemaTagUpdateParams(tag)])
  );
  const storableIds = getModifiedStorableIdsForTransfer(plateMap, grid, false);

  await callAsyncWithDataModifications({
    changeSummary: getInventoryRegistryChangeSummary({
      apiIds: storableIds,
      action: InventoryRegistryDataModAction.TRANSFER,
      containersMayBeWells: true,
    }),
    onPerformAction: async (dataModsHeader?) => {
      await withLoadingScreen(
        doUpdateFixedPlate({
          analyticsMetadata: {actionOrigin, eventGroupId},
          callbackOnError,
          dataModsHeader,
          data: {
            annotations: annotatePlateInputs,
            transferInputs: fillFixedPlateInputs,
            schemaFieldValues: schemaFieldValuesToSet,
            gridId: grid.id,
            plateRecordName,
            notePartIndex,
            appendStudyIds,
            plateDesignVersionRef,
            procedureMethodExecutionInstanceId,
          },
          fileId,
          grid,
          profilerEventMetadata: getProfilerMetadata({
            plateMap,
            numberSchemaFieldsAnnotated: annotatedSchemaFieldIds.size,
            fileId,
            isNewPlate: false,
            fromPlateDesign: plateDesignVersionRef != null,
          }),
        }),
        'Updating plate'
      );
      notifications.success('Annotated and filled plate');
    },
    onModalCancel: noop,
    analyticsParams: {
      module: 'SAMPLES',
      actionOrigin,
      modifiedAction: InventoryRegistryDataModAction.TRANSFER,
      numModifiedTotalItems: storableIds.length,
      entryId: null,
    },
    apiIds: storableIds,
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY,
  });
}
